import { LockKeyhole, Mail, ShieldCheck } from "lucide-react";
import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { requestPasswordReset, sendOtp, verifyOtp } from "../../services/auth/client";

type AuthPageProps = {
  mode: "login" | "register" | "forgot" | "verify" | "reset";
};

const copy = {
  login: {
    title: "Đăng nhập",
    summary: "Email + password hoặc Email OTP, OTP luôn đi qua Pages Function.",
    action: "Đăng nhập",
    alt: "Chưa có tài khoản?",
    altHref: "/register",
    altLabel: "Đăng ký"
  },
  register: {
    title: "Đăng ký",
    summary: "Tạo tài khoản B2C, có nền tảng nâng cấp B2B sau khi xét duyệt.",
    action: "Tạo tài khoản",
    alt: "Đã có tài khoản?",
    altHref: "/login",
    altLabel: "Đăng nhập"
  },
  forgot: {
    title: "Quên mật khẩu",
    summary: "Gửi yêu cầu đặt lại mật khẩu qua endpoint server-side.",
    action: "Gửi email khôi phục",
    alt: "Nhớ mật khẩu?",
    altHref: "/login",
    altLabel: "Đăng nhập"
  },
  verify: {
    title: "Xác minh OTP",
    summary: "Nhập OTP 6 số. OTP không sinh hoặc xác minh ở frontend.",
    action: "Xác minh",
    alt: "Cần gửi lại?",
    altHref: "/login",
    altLabel: "Quay về đăng nhập"
  },
  reset: {
    title: "Đặt lại mật khẩu",
    summary: "Mật khẩu mới sẽ được gửi đến backend khi có database/auth provider thật.",
    action: "Cập nhật mật khẩu",
    alt: "Đã hoàn tất?",
    altHref: "/login",
    altLabel: "Đăng nhập"
  }
} as const;

export function AuthPage({ mode }: AuthPageProps) {
  const [email, setEmail] = useState("khachhang@example.com");
  const [otp, setOtp] = useState("");
  const [status, setStatus] = useState("Sẵn sàng");
  const page = copy[mode];

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("Đang gửi yêu cầu...");

    try {
      if (mode === "verify") {
        const response = await verifyOtp(email, otp);
        setStatus(response.message ?? "OTP hợp lệ");
      } else if (mode === "forgot") {
        const response = await requestPasswordReset(email);
        setStatus(response.message ?? "Đã gửi email khôi phục");
      } else {
        const response = await sendOtp(email);
        setStatus(response.message ?? "Đã gửi OTP qua email");
      }
    } catch {
      setStatus("Endpoint chưa đủ binding/secret để xử lý yêu cầu này.");
    }
  };

  return (
    <main className="auth-page">
      <section className="auth-card">
        <div className="auth-side">
          <ShieldCheck aria-hidden="true" />
          <h1>{page.title}</h1>
          <p>{page.summary}</p>
          <div className="auth-rule">
            <strong>Rule khóa</strong>
            <span>OTP 6 số · expire 5-10 phút · dùng một lần · rate limit · retry limit</span>
          </div>
        </div>
        <form className="auth-form" onSubmit={onSubmit}>
          <label>
            Email
            <span>
              <Mail aria-hidden="true" />
              <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" />
            </span>
          </label>
          {mode !== "forgot" ? (
            <label>
              {mode === "verify" ? "OTP" : "Mật khẩu"}
              <span>
                <LockKeyhole aria-hidden="true" />
                <input
                  value={mode === "verify" ? otp : undefined}
                  onChange={mode === "verify" ? (event) => setOtp(event.target.value) : undefined}
                  type={mode === "verify" ? "text" : "password"}
                  placeholder={mode === "verify" ? "6 số" : "Mật khẩu"}
                  minLength={mode === "verify" ? 6 : 8}
                />
              </span>
            </label>
          ) : null}
          {mode === "register" ? (
            <label>
              Loại tài khoản
              <select defaultValue="b2c">
                <option value="b2c">Khách lẻ</option>
                <option value="b2b">B2B / Đại lý</option>
              </select>
            </label>
          ) : null}
          <button className="btn btn-primary" type="submit">
            <span>{page.action}</span>
          </button>
          <p className="auth-status">{status}</p>
          <p className="auth-alt">
            {page.alt} <Link to={page.altHref}>{page.altLabel}</Link>
          </p>
        </form>
      </section>
    </main>
  );
}
